import { useEffect, useRef } from 'react';
import { VideoPlayer } from '@zoom/videosdk';

import { useZoomCall } from 'hooks';

export const useParticipantHook = ({
  bVideoOn,
  isMain,
  userId
}: {
  bVideoOn: boolean;
  isMain?: boolean;
  userId: number;
}) => {
  const { room } = useZoomCall();
  const stream = room?.getMediaStream();
  const containerId = `${isMain ? 'main-' : 'local-'}${userId}`;

  // Track whether we've already attached the video to prevent duplicate calls
  const videoAttachedRef = useRef(false);

  const attachVideoIfNeeded = (container: HTMLElement) => {
    if (videoAttachedRef.current || !stream) return;

    videoAttachedRef.current = true;

    stream
      .attachVideo(userId, 3)
      .then((userVideo) => {
        if (!container.querySelector('video-player')) {
          container.appendChild(userVideo as VideoPlayer);
        }
      })
      .catch(() => {
        videoAttachedRef.current = false;
      });
  };

  const detachVideoIfNeeded = (container: HTMLElement) => {
    if (!videoAttachedRef.current || !stream) return;

    stream
      .detachVideo(userId)
      .catch(() => {})
      .finally(() => {
        container.querySelector('video-player')?.remove();
        videoAttachedRef.current = false;
      });
  };

  const cleanupVideo = () => {
    if (videoAttachedRef.current && stream) {
      stream
        .detachVideo(userId)
        .catch(() => {})
        .finally(() => {
          videoAttachedRef.current = false;
        });
    }
  };

  useEffect(() => {
    const container = document.getElementById(containerId);

    if (!container) return;

    if (bVideoOn) {
      attachVideoIfNeeded(container);
    } else {
      detachVideoIfNeeded(container);
    }

    return () => {
      cleanupVideo();
    };
  }, [bVideoOn, stream, userId, isMain]);

  return { containerId };
};
